<script>
  import { createEventDispatcher, onDestroy } from 'svelte';
  import {
    addMapping,
    listenToChannel,
    listenToChannelIds,
    removeMappingUrl,
    updateChannelProps,
    getMultichannelChatConfig,
    updateMultichannelChatConfig,
    setUserChatId,
  } from '../../../Api/api';
  import Dialog from '../../Common/Dialog/Dialog.svelte';
  import EditInput from '../../Common/EditInput/EditInput.svelte';
  import PulseStatusAnim from '../../Common/PulseStatusAnim.svelte';
  import { categories, firebaseApp } from '../../../Store/store';
  import { fade } from 'svelte/transition';
  import CustomPushMsg from './CustomPushMsg.svelte';

  const dispatch = createEventDispatcher();

  export let id = '';

  $: id && listenToChannelData(id);
  $: id && listenToMappings(id);

  let channel = null;
  let urls = [];
  let unsubscribeListenToChannelIds;
  let unsubscribeListenToChannel;
  let isLoadingMappings = false;
  $: isCheckingUrl = false;
  let showDialog = false;
  let multichannelConfig;

  $: dialogInputValue = '';

  $: if (channel?.category === 'multichannel') {
    getMultichannelConfigAsync();
  }

  const getMultichannelConfigAsync = async () => {
    let res = await getMultichannelChatConfig(channel.multichannel, channel.id);
    if (res) {
      multichannelConfig = res;
    }
  };

  const updateMultichannelChatUser = async (updateData, uidSet) => {
    multichannelConfig.user = { ...multichannelConfig.user, ...updateData };
    updateMultichannelChatConfig(channel.multichannel, channel.id, multichannelConfig);
    if (uidSet) {
      setUserChatId(multichannelConfig.user.uid, channel.id);
    }
  };

  const listenToMappings = async (channelId) => {
    isLoadingMappings = true;
    if (unsubscribeListenToChannelIds) {
      unsubscribeListenToChannelIds();
      urls = [];
      unsubscribeListenToChannelIds = null;
    }
    unsubscribeListenToChannelIds = await listenToChannelIds((docRefs) => {
      if (!docRefs.empty) {
        const cIds = docRefs.docs.map((d) => ({ ...d.data(), id: d.id }));
        for (let i = 0; i < cIds.length; i++) {
          const c = cIds[i];
          if (c.id === channelId) {
            urls = c.urls;
            break;
          }
        }
      }
      isLoadingMappings = false;
    });
  };

  const listenToChannelData = async (channelId) => {
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }
    unsubscribeListenToChannel = await listenToChannel(channelId, (docRef) => {
      if (docRef.exists) {
        channel = { ...docRef.data(), id: docRef.id };
      }
    });
  };

  onDestroy(() => {
    if (unsubscribeListenToChannelIds) {
      unsubscribeListenToChannelIds();
    }
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
    }
  });

  const onArchiveClicked = async (e, channel) => {
    e.stopPropagation();
    urls.forEach(async (url) => {
      await removeMappingUrl(channel.id, url);
    });
    await updateChannelProps(channel.id, {
      archived: !channel.archived,
      archiveTime: $firebaseApp.serverTimestamp,
    });
    if (channel.multichannel) {
      await updateMultichannelChatConfig(channel.multichannel, channel.id, {
        ...multichannelConfig,
        archived: !multichannelConfig?.archived,
      });
    }
    dispatch('channelarchived');
  };

  const onAddUrlClicked = async (e) => {
    e.stopPropagation();
    dialogInputValue = '';
    showDialog = true;
  };

  const onAddUrlDialogOk = async (e) => {
    isCheckingUrl = true;
    await addMapping(id, e.detail);
    dialogInputValue = '';
    showDialog = false;
    isCheckingUrl = false;
  };
  const onAddUrlDialogCancel = async () => {
    showDialog = false;
    isCheckingUrl = false;
  };

  const onDeleteUrlClicked = async (e, url) => {
    e.stopPropagation();
    await removeMappingUrl(id, url);
  };
</script>

<div class="chat-config-container" in:fade>
  {#if channel}
    <div class="settings">
      <div class="fields">
        <h1>Chat-rom innstillinger</h1>
        <div class="field">
          <span class="label">Navn</span>
          <EditInput
            value={channel.name}
            on:save={(e) => updateChannelProps(id, { name: e.detail })}
            large={true}
          />
        </div>
        <div class="field">
          <span class="label">Politisk</span>
          <div>
            <input
              class="checkbox"
              type="checkbox"
              checked={channel.political}
              on:change={(e) => updateChannelProps(id, { political: !channel.political })}
            />
          </div>
        </div>
        <div class="field">
          <span class="label">Kategori</span>
          <div class="select-container">
            <!-- svelte-ignore a11y-no-onchange -->
            <select
              bind:value={channel.category}
              on:change={(e) => updateChannelProps(id, { category: e.target.value })}
            >
              {#each $categories as category}
                {#if category.type !== 'all'}
                  <option value={category.type}>{category.displayName}</option>
                {/if}
              {/each}
            </select>
          </div>
        </div>
        <div class="field">
          <span class="label">Levetid (antall timer før arkivert chat slettes)</span>
          <EditInput
            value={channel.ttl || 3 * 24}
            on:save={(e) => updateChannelProps(id, { ttl: e.detail })}
            large={true}
          />
        </div>
        <div class="field">
          <span class="label">Arkiver</span>
          <button
            class="archive-button"
            on:click={(e) => onArchiveClicked(e, channel)}
            disabled={channel?.live}
          >
            <span class="material-icons">
              {channel?.archived ? 'unarchive' : 'archive'}
            </span>
            {channel?.archived ? 'Hent fra arkivet' : 'Arkiver chat'}
          </button>
        </div>
        <div class="field">
          <div class="url-mappings">
            <div class="header">
              <h4>URL'er hvor denne chat'en skal vises</h4>
              <button class="icon-button" on:click={onAddUrlClicked}>
                <span class="material-icons primary-icon"> add_circle </span>
              </button>
            </div>
            <div class="urls">
              {#if isLoadingMappings}
                <div class="spinner">
                  <PulseStatusAnim />
                </div>
              {/if}
              {#each urls as url}
                <div class="url">
                  <p>{url}</p>
                  <div class="btn-container">
                    <button class="icon-button" on:click={(e) => onDeleteUrlClicked(e, url)}>
                      <span class="material-icons"> cancel </span>
                    </button>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        </div>
      </div>

      {#if channel.category === 'sport'}
        <div class="fields">
          <h1 class="margin-top">Push-medlinger til Sportsappen</h1>
          <div class="field group">
            <header>
              <h3>Push melding når chat går live</h3>
              <input
                class="checkbox"
                type="checkbox"
                checked={channel.sendPushChatLive}
                on:change={(e) =>
                  updateChannelProps(id, {
                    sendPushChatLive: !channel.sendPushChatLive,
                  })}
              />
            </header>
            <span class="label">Tittel</span>
            <EditInput
              value={channel.pushTitle || 'Live chat nå!'}
              on:save={(e) => updateChannelProps(id, { pushTitle: e.detail })}
              large={true}
            />
            <span class="label">Beskrivelse</span>
            <EditInput
              value={channel.description || ''}
              on:save={(e) => updateChannelProps(id, { description: e.detail })}
              large={true}
            />
          </div>
          <div class="field group">
            <header>
              <h3>Push melding når video chat starter</h3>
              <input
                class="checkbox"
                type="checkbox"
                checked={channel.sendPushVideoLive}
                on:change={(e) =>
                  updateChannelProps(id, {
                    sendPushVideoLive: !channel.sendPushVideoLive,
                  })}
              />
            </header>
            <span class="label">Tittel</span>
            <EditInput
              value={channel.pushTitleVideoStart || '🤩 Live video chat!'}
              on:save={(e) => updateChannelProps(id, { pushTitleVideoStart: e.detail })}
              large={true}
            />
            <span class="label">Beskrivelse</span>
            <EditInput
              value={channel.pushDescriptionVideoStart || ''}
              on:save={(e) => updateChannelProps(id, { pushDescriptionVideoStart: e.detail })}
              large={true}
            />
          </div>
          <div class="field">
            <CustomPushMsg {channel} />
          </div>
        </div>
      {:else if channel.category === 'multichannel'}
        <div class="fields">
          <h2>Hovedbruker - Multichannel</h2>
          <div class="field">
            <span class="label">Navn</span>
            <EditInput
              value={multichannelConfig?.user?.name ? multichannelConfig.user.name : ''}
              on:save={(e) => updateMultichannelChatUser({ name: e.detail })}
              large={true}
            />
          </div>
          <div class="field">
            <span class="label">Kort navn</span>
            <EditInput
              value={multichannelConfig?.user?.shortName ? multichannelConfig.user.shortName : ''}
              on:save={(e) => updateMultichannelChatUser({ shortName: e.detail })}
              large={true}
            />
          </div>
          <div class="field">
            <span class="label">Organisasjon/lag</span>
            <EditInput
              value={multichannelConfig?.user?.organization
                ? multichannelConfig.user.organization
                : ''}
              on:save={(e) => updateMultichannelChatUser({ organization: e.detail })}
              large={true}
            />
          </div>
          <div class="field">
            <span class="label">Hovedbilde</span>
            <EditInput
              value={multichannelConfig?.user?.mainImageUrl
                ? multichannelConfig.user.mainImageUrl
                : ''}
              on:save={(e) => updateMultichannelChatUser({ mainImageUrl: e.detail })}
              large={true}
            />
          </div>
          <div class="field">
            <span class="label">Sekundærbilde</span>
            <EditInput
              value={multichannelConfig?.user?.secondaryImageUrl
                ? multichannelConfig.user.secondaryImageUrl
                : ''}
              on:save={(e) => updateMultichannelChatUser({ secondaryImageUrl: e.detail })}
              large={true}
            />
          </div>
          <div class="field">
            <span class="label">Brukerid</span>
            <EditInput
              value={multichannelConfig?.user?.uid ? multichannelConfig.user.uid : ''}
              on:save={(e) => updateMultichannelChatUser({ uid: e.detail }, true)}
              large={true}
            />
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<Dialog
  open={showDialog}
  on:cancel={onAddUrlDialogCancel}
  on:ok={onAddUrlDialogOk}
  isLoading={isCheckingUrl}
  placeholder="url"
  title="Legg til ny URL"
  inputValue={dialogInputValue}
/>

<style src="./chatConfig.scss">
</style>
